import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { Printer, Download, Pencil, Trash2 } from 'lucide-react';
import { billsAPI } from '../../services/api';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import { useCompany } from '../common/useCompany';
import { printNode } from '../../utils/print';
import PrintButton from '../../utils/print';
import DocumentTemplate from '../common/DocumentTemplate';
import PrintLetterhead from '../common/PrintLetterhead';
import EmailModal from '../common/EmailModal';
import { generateBillPDF } from '../../utils/generateDocumentPDF';
import { makeMoneyFormatter, getDisplayCurrency, convertAmount } from '../../utils/currency';

const fmtDoc = (code) => makeMoneyFormatter(code, { currencyDisplay: 'code' });

export default function BillList() {
  const { hasPermission } = useAuth();
  const company = useCompany();
  const navigate = useNavigate();
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');
  const listRef = useRef(null);

  const baseCurrency = company?.base_currency || getDisplayCurrency();
  const fmtBase = fmtDoc(baseCurrency);

  // Print preview state
  const [selectedBill, setSelectedBill] = useState(null);
  const [billDetail, setBillDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [showEmail, setShowEmail] = useState(false);
  const printDocRef = useRef(null);

  useEffect(() => {
    billsAPI.list({ status: status || undefined }).then(setBills).catch(e => toast.error(e.message)).finally(() => setLoading(false));
  }, [status]);

  const handleRowClick = async (bill) => {
    setLoadingDetail(true);
    setSelectedBill(bill);
    try {
      const detail = await billsAPI.get(bill.id);
      setBillDetail(detail);
    } catch (err) {
      toast.error(err.message);
      setSelectedBill(null);
    }
    setLoadingDetail(false);
  };

  const doDelete = async (e, b) => {
    e.stopPropagation();
    if (!window.confirm(`Delete bill ${b.bill_number}? This cannot be undone.`)) return;
    try {
      await billsAPI.remove(b.id);
      toast.success('Bill deleted');
      load();
    } catch (err) { toast.error(err.message); }
  };

  return (
    <div>
      <div className="toolbar"><h2>Bills</h2><div className="toolbar-right">
        <PrintButton targetRef={listRef} title={`Bills - ${new Date().toLocaleDateString()}`} />
        {hasPermission('bills:create') && <button className="btn btn-primary" onClick={() => navigate('/bills/new')}>+ New Bill</button>}
      </div></div>
      <div className="filters-bar">
        {['','Draft','Submitted','Partial','Paid','Overdue','Voided'].map(s => (<button key={s} className={`btn btn-sm ${status===s?'btn-primary':'btn-outline'}`} onClick={()=>setStatus(s)}>{s||'All'}</button>))}
      </div>
      {loading ? <div className="spinner" style={{margin:'40px auto'}} /> : (
        <div className="card" ref={listRef}><div className="card-body" style={{padding:0}}>
          <PrintLetterhead />
          <table>
            <thead><tr><th>Bill #</th><th>Vendor</th><th>Date</th><th>Due Date</th><th className="text-right">Total</th><th className="text-right">Paid</th><th className="text-right">Due</th><th>Status</th><th className="text-right">Actions</th></tr></thead>
            <tbody>
              {bills.map(b => (<tr key={b.id} onClick={() => handleRowClick(b)} style={{cursor:'pointer'}}>
                <td className="font-mono">{b.bill_number}</td><td>{b.vendor_name}</td><td>{b.bill_date}</td><td>{b.due_date}</td>
                <td className="amount">
                  {fmtDoc(b.currency)(b.total)}
                  {b.currency && b.currency !== baseCurrency && parseFloat(b.total) > 0 && (
                    <div className="fx-sub">≈ {fmtBase(convertAmount(b.total, b.exchange_rate))}</div>
                  )}
                </td><td className="amount">{fmtDoc(b.currency)(b.amount_paid)}</td>
                <td className={`amount ${parseFloat(b.amount_due)>0?'negative':''}`}>{fmtDoc(b.currency)(b.amount_due)}
                  {b.currency && b.currency !== baseCurrency && parseFloat(b.amount_due) > 0 && (
                    <div className="fx-sub">≈ {fmtBase(convertAmount(b.amount_due, b.exchange_rate))}</div>
                  )}
                </td>
                <td><span className={`badge badge-${b.status?.toLowerCase()}`}>{b.status}</span></td>
                <td className="text-right" onClick={e => e.stopPropagation()} style={{whiteSpace:'nowrap'}}>
                  <div style={{display:'flex',gap:4,justifyContent:'flex-end'}}>
                    {hasPermission('bills:edit') && (
                      <button className="btn btn-ghost btn-sm" title="Edit" onClick={() => navigate(`/bills/${b.id}`)}><Pencil size={13} /></button>
                    )}
                    {hasPermission('bills:edit') && b.status === 'Draft' && (
                      <button className="btn btn-ghost btn-sm" title="Delete" onClick={(e) => doDelete(e, b)}><Trash2 size={13} /></button>
                    )}
                  </div>
                </td>
              </tr>))}
              {bills.length === 0 && <tr><td colSpan={9} className="text-center text-muted" style={{padding:30}}>No bills found</td></tr>}
            </tbody>
          </table>
        </div></div>
      )}

      {/* Print Preview Modal */}
      {selectedBill && createPortal(
        <div className="modal-overlay" onClick={() => { setSelectedBill(null); setBillDetail(null); }} style={{ zIndex: 1000 }}>
          <div className="modal" style={{ maxWidth: 800, maxHeight: '90vh', overflow: 'auto' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Bill {selectedBill.bill_number}</h3>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={() => { if (printDocRef.current) printNode(printDocRef.current, `Bill ${selectedBill.bill_number}`); }}
                >
                  <Printer size={13} style={{ verticalAlign: '-2px', marginRight: 4 }} /> Print
                </button>
                <button
                  type="button"
                  className="btn btn-outline btn-sm"
                  onClick={async () => { if (billDetail) { try { await generateBillPDF(billDetail, company); } catch(e) { toast.error('PDF failed: ' + e.message); } } }}
                >
                  <Download size={13} style={{ verticalAlign: '-2px', marginRight: 4 }} /> PDF
                </button>
                <button type="button" className="btn btn-outline btn-sm" onClick={() => { setShowEmail(true); }}>
                  ✉ Email
                </button>
                <button type="button" className="btn btn-outline btn-sm" onClick={() => { setSelectedBill(null); setBillDetail(null); navigate(`/bills/${selectedBill.id}`); }}>
                  Edit
                </button>
                <button type="button" className="btn-ghost" onClick={() => { setSelectedBill(null); setBillDetail(null); }}>✕</button>
              </div>
            </div>
            <div className="modal-body" style={{ padding: 0 }}>
              {loadingDetail ? (
                <div style={{ padding: 40, textAlign: 'center' }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
              ) : billDetail ? (
                <div ref={printDocRef} className="print-preview" style={{ padding: 20 }}>
                  <DocumentTemplate
                    docType="bill"
                    data={{
                      bill_number: billDetail.bill_number,
                      bill_date: billDetail.bill_date,
                      due_date: billDetail.due_date,
                      status: billDetail.status,
                      currency: billDetail.currency,
                      exchange_rate: billDetail.exchange_rate,
                      vendor_name: billDetail.vendor_name,
                      vendor_email: billDetail.vendor_email,
                      vendor_phone: billDetail.vendor_phone,
                      subtotal: billDetail.subtotal,
                      tax_amount: billDetail.tax_amount,
                      total: billDetail.total,
                      amount_paid: billDetail.amount_paid,
                      amount_due: billDetail.amount_due,
                      memo: billDetail.memo || billDetail.notes,
                      items: billDetail.items,
                    }}
                  />
                </div>
              ) : null}
            </div>
          </div>
        </div>,
        document.body
      )}

      <EmailModal
        show={showEmail}
        onClose={() => setShowEmail(false)}
        type="bill"
        refId={selectedBill?.id}
        number={selectedBill?.bill_number}
        recipientEmail={billDetail?.vendor_email || ''}
        recipientName={billDetail?.vendor_name || ''}
      />
    </div>
  );
}
